import React, { useState } from "react";
import { Input } from "../ui/input";
import { Plus, Trash2, ChevronDown, ChevronRight, Check } from "lucide-react";
import RawNumInput from "./RawNumInput";
import { UNIT_LABEL } from "../../lib/calculator";
import { autoSelectOnFocus } from "../../lib/autoNameSelect";

// v6 F42j — Fase 2c Correctieronde: aanvullende buitenelementen binnen een
// buitenzone (dakoversteek, boeiboord, kroonlijst, ander). Deze elementen
// tellen NIET op bij gevel-m² (compute_zone_area negeert facade_extra_elements).
//
// Toevoegingen tov basis-2c:
//   - Materiaalkeuze (hout / beton-steen / metaal / pvc / ander)
//   - Unit-specifieke invoervelden:
//       lm      → dim_length
//       m²      → calc_mode toggle: L×B  |  handmatige m²
//       stuk    → qty
//       forfait → gelockt op 1
//   - Slimme prijscode-filter (unit + materiaal)
//   - Veilige unit/materiaal-switch: incompatibele werkposten worden gewist,
//     overrides op nog geldige werken blijven behouden.

export const ELEMENT_KINDS = [
  { value: "dakoversteek", label: "Dakoversteek",       default_unit: "m2", allowed_units: ["m2", "forfait"],
    allowed_materials: ["hout", "beton_steen", "metaal", "pvc", "composiet", "ander"] },
  { value: "boeiboord",    label: "Boeiboord",          default_unit: "lm", allowed_units: ["lm", "forfait"],
    allowed_materials: ["hout", "metaal", "pvc", "composiet", "ander"] },  // geen beton/steen
  { value: "kroonlijst",   label: "Kroonlijst",         default_unit: "lm", allowed_units: ["lm", "forfait"],
    allowed_materials: ["hout", "beton_steen", "metaal", "pvc", "composiet", "ander"] },
  { value: "raamlijst",    label: "Raamlijst",          default_unit: "lm", allowed_units: ["lm", "forfait"],
    allowed_materials: ["hout", "beton_steen", "metaal", "pvc", "composiet", "ander"] },
  { value: "dorpel",       label: "Dorpel",             default_unit: "lm", allowed_units: ["lm", "forfait"],
    allowed_materials: ["hout", "beton_steen", "metaal", "pvc", "composiet", "ander"] },
  { value: "other",        label: "Ander buitenelement", default_unit: "lm", allowed_units: ["lm", "m2", "stuks", "forfait"],
    allowed_materials: ["hout", "beton_steen", "metaal", "pvc", "composiet", "ander"] },
];
const UNITS = [
  { value: "lm",      label: "lm" },
  { value: "m2",      label: "m²" },
  { value: "stuks",   label: "stuk" },
  { value: "forfait", label: "forfait" },
];

export const MATERIALS = [
  { value: "",            label: "— Materiaal —" },
  { value: "hout",        label: "Hout" },
  { value: "beton_steen", label: "Steen / beton" },
  { value: "metaal",      label: "Metaal / aluminium" },
  { value: "pvc",         label: "PVC / kunststof" },
  { value: "composiet",   label: "Composiet" },
  { value: "ander",       label: "Ander (specifiëren)" },
];

// v6 F42j — Fase 2c v4: strikte materiaal-filter + choice_group voor
// dakoversteek-eindafwerking. Elk WORK_ITEM heeft een verplichte `materials`-
// tag (behalve legacy `other::*` sets). Choice-group "dakoversteek_finish"
// zorgt dat beitsen/vernissen/lakken/schilderen/kaleien wederzijds
// uitsluitend zijn per element.
export const KIND_LABEL = {
  dakoversteek: "Dakoversteek",
  boeiboord:    "Boeiboord",
  kroonlijst:   "Kroonlijst",
  raamlijst:    "Raamlijst",
  dorpel:       "Dorpel",
  other:        null,
};

const CG_DAKOVERSTEEK_FINISH = "dakoversteek_finish";
const CG_EXTRA_SANDING = "extra_element_sanding";

// Human-readable label per choice-group (getoond als sectie-header in de
// werkposten-lijst). Toevoegen wanneer een nieuwe groep wordt geïntroduceerd.
export const CHOICE_GROUP_LABELS = {
  [CG_DAKOVERSTEEK_FINISH]: "Eindafwerking — kies één",
  [CG_EXTRA_SANDING]:       "Schuurgraad — kies één",
};

// Helper voor description-override — gebruikt kind-label als prefix. Voor
// element.kind="other" (Ander buitenelement) blijft de PC-description ongewijzigd.
const ov = (verb) => (kind) => (KIND_LABEL[kind] ? `${KIND_LABEL[kind]} ${verb}` : null);

const WORK_ITEMS = {
  "dakoversteek::lm": [
    { code: "S-06.50", materials: ["hout", "metaal", "pvc"] },       // reinigen
    { code: "S-06.51", materials: ["hout", "metaal"] },               // schuren
    { code: "S-06.52", materials: ["hout", "metaal", "pvc"] },        // gronden/primer
    // Eindafwerking — exclusieve choice_group per element
    { code: "S-06.53", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH },   // beitsen (hout)
    { code: "S-06.69", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("vernissen") }, // vernissen (hout) — v6 F42k Correctieronde
    { code: "S-06.54", materials: ["hout", "metaal", "pvc"], choice_group: CG_DAKOVERSTEEK_FINISH }, // lakken/schilderen
  ],
  "dakoversteek::m2": [
    // HOUT — reinigen · licht/intensief schuren · primer · [beitsen | vernissen | lakken]
    { code: "S-06.01", materials: ["hout"], override: ov("reinigen") },
    { code: "S-06.64", materials: ["hout"], choice_group: CG_EXTRA_SANDING },       // licht schuren
    { code: "S-06.65", materials: ["hout"], choice_group: CG_EXTRA_SANDING },       // intensief schuren
    { code: "S-06.02", materials: ["hout"], override: ov("gronden / primer") },
    { code: "S-06.60", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH }, // beitsen
    { code: "S-06.61", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH }, // vernissen
    { code: "S-06.29", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("lakken") }, // lakken (was "onderzijde lakken")
    // METAAL — reinigen/ontvetten · ontroesten/schuren · corrosiewerende primer · lakken
    { code: "S-06.01", materials: ["metaal"], override: ov("reinigen / ontvetten") },
    { code: "S-06.62", materials: ["metaal"], override: ov("ontroesten / schuren") },
    { code: "S-06.02", materials: ["metaal"], override: ov("corrosiewerende primer") },
    { code: "S-06.29", materials: ["metaal"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("lakken") },
    // PVC — reinigen/ontvetten · licht schuren/matteren · kunststofprimer · schilderen
    { code: "S-06.01", materials: ["pvc"], override: ov("reinigen / ontvetten") },
    { code: "S-06.63", materials: ["pvc"], override: ov("licht schuren / matteren") },
    { code: "S-06.02", materials: ["pvc"], override: ov("kunststofprimer") },
    { code: "S-06.03", materials: ["pvc"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("schilderen") },
    // BETON / STEEN — reinigen · ontmossen · herstellen · fixeren/primer · [schilderen | kaleien]
    { code: "S-06.01", materials: ["beton_steen"], override: ov("reinigen") },
    { code: "S-06.08", materials: ["beton_steen"], override: ov("ontmossen") },
    { code: "S-06.67", materials: ["beton_steen"], override: ov("plaatselijk herstellen") },
    { code: "S-06.02", materials: ["beton_steen"], override: ov("fixeren / primer") },
    { code: "S-06.03", materials: ["beton_steen"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("schilderen met gevelverf") },
    { code: "S-06.05", materials: ["beton_steen"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("kaleien") },
    // ANDER — generieke voorbereiding + afwerking (choice-group blijft actief)
    { code: "S-06.01", materials: ["ander"], override: ov("reinigen") },
    { code: "S-06.64", materials: ["ander"], override: ov("schuren") },
    { code: "S-06.02", materials: ["ander"], override: ov("primer") },
    { code: "S-06.29", materials: ["ander"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("lakken") },
    { code: "S-06.03", materials: ["ander"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("schilderen") },
  ],
  "boeiboord::lm": [
    { code: "S-06.55", materials: ["hout", "metaal", "pvc"] },       // reinigen
    { code: "S-06.56", materials: ["hout", "metaal"] },               // schuren
    { code: "S-06.57", materials: ["hout", "metaal", "pvc"] },        // gronden/primer
    // Eindafwerking — exclusieve choice_group per element (v6 F42k Correctieronde)
    { code: "S-06.68", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("beitsen") },   // beitsen (hout)
    { code: "S-06.69", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("vernissen") }, // vernissen (hout)
    { code: "S-06.58", materials: ["hout", "metaal", "pvc"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("schilderen / lakken") }, // lakken/schilderen
  ],
  "boeiboord::m2": [
    { code: "S-06.30", materials: ["hout", "metaal", "pvc"] },
    { code: "S-06.01", materials: ["beton_steen"], override: ov("reinigen") },
    { code: "S-06.08", materials: ["beton_steen"], override: ov("ontmossen") },
    { code: "S-06.02", materials: ["beton_steen"], override: ov("fixeren / primer") },
    { code: "S-06.03", materials: ["beton_steen"], override: ov("schilderen met gevelverf") },
    { code: "S-06.05", materials: ["beton_steen"], override: ov("kaleien") },
  ],
  "kroonlijst::lm": [
    { code: "S-06.55", materials: ["hout", "metaal", "pvc"] },       // reinigen
    { code: "S-06.56", materials: ["hout", "metaal"] },               // schuren
    { code: "S-06.57", materials: ["hout", "metaal", "pvc"] },        // gronden/primer
    // Eindafwerking — exclusieve choice_group per element (v6 F42k Correctieronde)
    { code: "S-06.68", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("beitsen") },   // beitsen (hout)
    { code: "S-06.69", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("vernissen") }, // vernissen (hout)
    { code: "S-06.58", materials: ["hout", "metaal", "pvc"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("schilderen / lakken") }, // lakken/schilderen
  ],
  "kroonlijst::m2": [
    { code: "S-06.30", materials: ["hout", "metaal", "pvc"] },
    { code: "S-06.01", materials: ["beton_steen"], override: ov("reinigen") },
    { code: "S-06.08", materials: ["beton_steen"], override: ov("ontmossen") },
    { code: "S-06.02", materials: ["beton_steen"], override: ov("fixeren / primer") },
    { code: "S-06.03", materials: ["beton_steen"], override: ov("schilderen met gevelverf") },
    { code: "S-06.05", materials: ["beton_steen"], override: ov("kaleien") },
  ],
  // Raamlijst / Dorpel — lm-set met exclusieve eindafwerking per element (v6 F42k Correctieronde).
  // Description wordt automatisch geprefixt via KIND_LABEL (Raamlijst / Dorpel).
  "raamlijst::lm": [
    { code: "S-06.55", materials: ["hout", "metaal", "pvc", "composiet"], override: ov("reinigen") },
    { code: "S-06.56", materials: ["hout", "metaal"],                     override: ov("schuren") },
    { code: "S-06.57", materials: ["hout", "metaal", "pvc", "composiet"], override: ov("primer") },
    // Eindafwerking — exclusieve choice_group
    { code: "S-06.68", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("beitsen") },
    { code: "S-06.69", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("vernissen") },
    { code: "S-06.58", materials: ["hout", "metaal", "pvc", "composiet"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("schilderen / lakken") },
  ],
  "dorpel::lm": [
    { code: "S-06.55", materials: ["hout", "metaal", "pvc", "composiet"], override: ov("reinigen") },
    { code: "S-06.56", materials: ["hout", "metaal"],                     override: ov("schuren") },
    { code: "S-06.57", materials: ["hout", "metaal", "pvc", "composiet"], override: ov("primer") },
    // Eindafwerking — exclusieve choice_group
    { code: "S-06.68", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("beitsen") },
    { code: "S-06.69", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("vernissen") },
    { code: "S-06.58", materials: ["hout", "metaal", "pvc", "composiet"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("schilderen / lakken") },
    { code: "S-06.01", materials: ["beton_steen"],                        override: ov("reinigen") },
    { code: "S-06.02", materials: ["beton_steen"],                        override: ov("fixeren / primer") },
    { code: "S-06.03", materials: ["beton_steen"], choice_group: CG_DAKOVERSTEEK_FINISH, override: ov("schilderen met gevelverf") },
  ],
  // "Ander buitenelement" — geen kind-prefix; overrides worden onderdrukt
  // (KIND_LABEL.other = null → ov() geeft null → PC-description blijft).
  "other::lm": [
    { code: "S-06.50" }, { code: "S-06.51" }, { code: "S-06.52" },
    // Eindafwerking — exclusieve choice_group (v6 F42k Correctieronde)
    { code: "S-06.68", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH },  // beitsen
    { code: "S-06.69", materials: ["hout"], choice_group: CG_DAKOVERSTEEK_FINISH },  // vernissen
    { code: "S-06.54", choice_group: CG_DAKOVERSTEEK_FINISH },                        // lakken
    { code: "S-06.55" }, { code: "S-06.56" }, { code: "S-06.57" }, { code: "S-06.58" },
  ],
  "other::m2": [
    { code: "S-06.29" }, { code: "S-06.30" },
  ],
  // Forfait — 1 generieke code per kind. Description krijgt element.name als
  // prefix (via toggleWork → measurement.description). User bepaalt de prijs
  // zelf via unit_price_override.
  "dakoversteek::forfait": [{ code: "S-06.90", override: (kind, elem) => `${(elem?.name?.trim() || KIND_LABEL[kind] || "Aanvullend element")} — forfait` }],
  "boeiboord::forfait":    [{ code: "S-06.90", override: (kind, elem) => `${(elem?.name?.trim() || KIND_LABEL[kind] || "Aanvullend element")} — forfait` }],
  "kroonlijst::forfait":   [{ code: "S-06.90", override: (kind, elem) => `${(elem?.name?.trim() || KIND_LABEL[kind] || "Aanvullend element")} — forfait` }],
  "raamlijst::forfait":    [{ code: "S-06.90", override: (kind, elem) => `${(elem?.name?.trim() || KIND_LABEL[kind] || "Aanvullend element")} — forfait` }],
  "dorpel::forfait":       [{ code: "S-06.90", override: (kind, elem) => `${(elem?.name?.trim() || KIND_LABEL[kind] || "Aanvullend element")} — forfait` }],
  "other::forfait":        [{ code: "S-06.90", override: (kind, elem) => `${(elem?.name?.trim() || "Aanvullend element")} — forfait` }],
};

// Materiaal-filter: v4 = STRIKT. Als item.materials leeg is → altijd
// toegestaan (bv. legacy other::* items). Wanneer materiaal="" (nog niet
// gekozen) → GEEN materiaal-gebonden items tonen. Voor specifiek materiaal
// → alleen items met dat materiaal in de lijst.
function isItemAllowed(item, material) {
  const mats = item.materials;
  if (!mats || mats.length === 0) return true;
  if (!material) return false; // material="" → verberg alle materiaal-gebonden items
  return mats.includes(material);
}

// Bouw voor een gegeven element (kind + unit + material) de effectieve lijst
// werken → geeft objecten met resolved PC + description-override + optionele
// choice_group. PC's die (nog) niet bestaan in de prijslijst worden gefilterd.
// Duplicates op pc.id worden ontdubbeld (defensive; met strikte filter is
// dit normaliter niet nodig maar beschermt legacy scenarios).
export function resolveWorkItems(kind, unit, material, priceCodes, elem) {
  const key = `${kind}::${unit}`;
  const items = WORK_ITEMS[key] || [];
  const resolved = items
    .filter((it) => isItemAllowed(it, material))
    .map((it) => {
      const pc = priceCodes.find((p) => p.code === it.code && p.active);
      if (!pc) return null;
      const override = it.override ? it.override(kind, elem) : null;
      return {
        pc,
        override,
        display: override || pc.description,
        choice_group: it.choice_group || null,
      };
    })
    .filter(Boolean);
  const seen = new Set();
  return resolved.filter((r) => {
    if (seen.has(r.pc.id)) return false;
    seen.add(r.pc.id);
    return true;
  });
}

// Effectieve qty afleiden uit unit + calc_mode + dimensies. Legacy records
// (waar dim_length/dim_width/manual_area = 0) vallen terug op elem.qty.
export function computeElementQty(el) {
  const unit = el?.unit || "lm";
  const qLegacy = +el?.qty || 0;
  if (unit === "lm") {
    return (+el?.dim_length || 0) || qLegacy;
  }
  if (unit === "m2") {
    const mode = el?.calc_mode || "lxw";
    if (mode === "manual") {
      return (+el?.manual_area || 0) || qLegacy;
    }
    const L = +el?.dim_length || 0;
    const W = +el?.dim_width || 0;
    const v = L * W;
    return v > 0 ? v : qLegacy;
  }
  if (unit === "stuks") {
    return qLegacy;
  }
  if (unit === "forfait") {
    return 1;
  }
  return qLegacy;
}

export function fmtEUR(v) {
  const n = +v || 0;
  return `€ ${n.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// --- Één werkpost regel voor een buitenelement (compact) ---
// Mobiel (<640px): checkbox + volledige omschrijving + code/categorie + mobiele
// samenvatting "[qty] [unit] × € [prijs]/[unit]" + regelbedrag onder qty.
// Prijs bewerken op mobiel: "Prijs aanpassen"-knop klapt inline een invoerveld
// open (zelfde patroon als joinery/gevel).
// Desktop (sm+): volledig behouden — unit-, prijs-, qty- en total-kolommen.
function ExtraWorkLine({ pc, displayDescription, measurement, elementQty, unitLabel, zoneId, elemId, onToggle, onUpdateMeasurement }) {
  const checked = !!measurement;
  const defaultPrice = pc ? +pc.default_price : 0;
  const measurementOverride = checked && measurement?.unit_price_override != null && measurement.unit_price_override !== ""
    ? +measurement.unit_price_override
    : null;
  const unitPrice = measurementOverride ?? defaultPrice;
  const lineTotal = checked ? +elementQty * unitPrice : 0;
  const shownDesc = displayDescription || pc?.description || "—";
  const [mobilePriceOpen, setMobilePriceOpen] = useState(false);

  const priceInput = (
    <>
      <span className="text-[11px] text-[#4F4F4D]">€</span>
      <RawNumInput
        value={measurementOverride != null ? measurementOverride : ""}
        allowEmpty
        onChange={(v) => {
          if (v === "" || v === null || v === undefined) {
            onUpdateMeasurement(measurement.id, { unit_price_override: null });
          } else {
            onUpdateMeasurement(measurement.id, { unit_price_override: +v });
          }
        }}
        placeholder={defaultPrice.toFixed(2)}
        className="w-20 h-8 rounded-md text-right text-sm"
        data-testid={`extra-work-price-${zoneId}-${elemId}-${pc?.code}`}
      />
      {measurementOverride != null && (
        <span
          className="text-[9px] uppercase tracking-wider font-bold text-[#B07A4F]"
          data-testid={`extra-work-price-override-badge-${zoneId}-${elemId}-${pc?.code}`}
          title="Aangepast voor dit project — centrale prijslijst blijft ongewijzigd"
        >
          Aangepast
        </span>
      )}
    </>
  );

  return (
    <div
      className={`flex items-start sm:items-center gap-3 px-3 py-2 ${checked ? "bg-[#FFF8F2]" : "hover:bg-[#FAF9F6]"}`}
      data-testid={`extra-work-row-${zoneId}-${elemId}-${pc?.code}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition mt-0.5 sm:mt-0 ${checked ? "text-white border-[#6B8E6B] bg-[#6B8E6B]" : "border-[#C9C8C2] hover:border-[#6B8E6B]"}`}
        data-testid={`extra-work-toggle-${zoneId}-${elemId}-${pc?.code}`}
        aria-label={checked ? "Werk verwijderen" : "Werk toevoegen"}
      >
        {checked && <Check className="w-3.5 h-3.5" />}
      </button>

      {/* Description block — bevat mobiele samenvatting + prijs-editor */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#2C2C2A] line-clamp-2 sm:truncate">{shownDesc}</div>
        <div className="text-[10px] text-[#5D7259] font-mono truncate">
          {pc?.code} · {pc?.work_category}
        </div>

        {/* Mobiele samenvatting — "qty unit × € prijs/unit" */}
        <div
          className="sm:hidden text-[11px] font-mono text-[#2C2C2A] mt-1"
          data-testid={`extra-work-summary-mobile-${zoneId}-${elemId}-${pc?.code}`}
        >
          {(+elementQty || 0).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel} × {fmtEUR(unitPrice)}/{unitLabel}
        </div>

        {/* Mobiele prijs-editor — verschijnt na tik op "Prijs aanpassen" */}
        {checked && onUpdateMeasurement && (
          <div className="sm:hidden mt-1.5">
            {!mobilePriceOpen ? (
              <button
                type="button"
                onClick={() => setMobilePriceOpen(true)}
                className="text-[11px] font-medium text-[#7F4124] underline decoration-dotted"
                data-testid={`extra-work-price-edit-mobile-${zoneId}-${elemId}-${pc?.code}`}
              >
                {measurementOverride != null ? "Prijs wijzigen" : "Prijs aanpassen"}
              </button>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                {priceInput}
                <button
                  type="button"
                  onClick={() => setMobilePriceOpen(false)}
                  className="text-[11px] text-[#5D7259] underline"
                  data-testid={`extra-work-price-close-mobile-${zoneId}-${elemId}-${pc?.code}`}
                >
                  Klaar
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Unit — desktop */}
      <div className="text-[11px] text-[#4F4F4D] hidden sm:block w-12 text-right">{unitLabel}</div>

      {/* Prijs — desktop */}
      <div className="hidden sm:flex flex-wrap items-center gap-x-1 gap-y-0.5 w-24 justify-end">
        {checked && onUpdateMeasurement ? priceInput : (
          <span className="text-[11px] text-[#4F4F4D]">€ {unitPrice.toFixed(2)}/{unitLabel}</span>
        )}
      </div>

      {/* Qty — mobiel + desktop. Mobiel: totaal onder qty. */}
      <div className="w-20 text-right flex-shrink-0">
        <div
          className="text-sm font-bold text-[#2C2C2A]"
          data-testid={`extra-work-qty-${zoneId}-${elemId}-${pc?.code}`}
        >
          {(+elementQty || 0).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}
        </div>
        <div
          className={`sm:hidden text-sm font-bold mt-0.5 ${checked ? "text-[#6B8E6B]" : "text-[#A8A7A0]"}`}
          data-testid={`extra-work-total-mobile-${zoneId}-${elemId}-${pc?.code}`}
        >
          {fmtEUR(checked ? lineTotal : 0)}
        </div>
      </div>

      {/* Total — desktop */}
      <div className="w-24 text-right hidden sm:block">
        <div
          className={`text-sm font-bold ${checked ? "text-[#6B8E6B]" : "text-[#A8A7A0]"}`}
          data-testid={`extra-work-total-${zoneId}-${elemId}-${pc?.code}`}
        >
          {fmtEUR(lineTotal)}
        </div>
      </div>
    </div>
  );
}

// --- Één buitenelement (inklapbaar, met werkposten) ---
function FacadeExtraElementCard({ elem, index, priceCodes, measurements, zoneId, defaultCollapsed, onUpdate, onRemove, onAddMeasurement, onUpdateMeasurement, onRemoveMeasurement }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const kindLabel = ELEMENT_KINDS.find((k) => k.value === elem.kind)?.label || elem.kind;
  const unit = elem.unit || "lm";
  const unitLabel = UNIT_LABEL[unit] || unit;
  const displayName = elem.name?.trim() || `${kindLabel} ${index + 1}`;
  const calcMode = elem.calc_mode || "lxw";
  const material = elem.material || "";

  const effectiveQty = computeElementQty(elem);

  // Lookup applicable works — filter op unit én materiaal (Fase 2c Correctieronde v2)
  const worksForElem = resolveWorkItems(elem.kind, unit, material, priceCodes, elem);

  // Fase 2c v3 — Beschikbare eenheden per kind. Legacy elementen (bv. een
  // opgeslagen dakoversteek met unit=lm) blijven zichtbaar in de toggle
  // ("(legacy)") zodat backwards-compat gewaarborgd is. Nieuwe kinds
  // (dakoversteek/boeiboord/kroonlijst) tonen enkel logische eenheden.
  const kindDef = ELEMENT_KINDS.find((k) => k.value === elem.kind);
  const allowedUnits = kindDef?.allowed_units || UNITS.map((u) => u.value);
  const visibleUnits = allowedUnits.includes(unit)
    ? allowedUnits
    : [...allowedUnits, unit]; // legacy unit blijft zichtbaar

  // Fase 2c v5 — Beschikbare materialen per kind (bv. Boeiboord kent geen
  // Beton/steen). Legacy elementen met een niet meer toegelaten materiaal
  // blijven backward-compatible zichtbaar met "(legacy)" suffix.
  const allowedMaterials = kindDef?.allowed_materials || MATERIALS.map((m) => m.value);
  const visibleMaterials = (material && !allowedMaterials.includes(material))
    ? [...MATERIALS.map((m) => m.value).filter((v) => allowedMaterials.includes(v) || v === ""), material]
    : MATERIALS.map((m) => m.value).filter((v) => allowedMaterials.includes(v) || v === "");

  const measByPc = Object.fromEntries(
    measurements
      .filter((m) => m.extra_element_id === elem.id && m.zone_id === zoneId)
      .map((m) => [m.price_code_id, m])
  );

  const toggleWork = (item) => {
    const { pc, override, choice_group } = item;
    const savedOverrides = { ...(elem.saved_price_overrides || {}) };
    const existing = measByPc[pc.id];
    if (existing) {
      // Bewaar override vóór verwijderen zodat gebruiker de prijs niet
      // opnieuw hoeft in te tikken bij re-select (Fase 2c v6).
      if (existing.unit_price_override != null && existing.unit_price_override !== "") {
        savedOverrides[pc.id] = +existing.unit_price_override;
        onUpdate({ saved_price_overrides: savedOverrides });
      }
      onRemoveMeasurement(existing.id);
      return;
    }
    // Choice-group: bij aanvinken van een eindafwerking (bv. beitsen) worden
    // de andere afwerkingen in dezelfde groep (vernissen/lakken/schilderen/
    // kaleien) automatisch uitgezet — max één actief tegelijk.
    let overridesChanged = false;
    if (choice_group) {
      const conflicting = worksForElem.filter(
        (w) => w.choice_group === choice_group && w.pc.id !== pc.id
      );
      for (const w of conflicting) {
        const meas = measByPc[w.pc.id];
        if (meas) {
          if (meas.unit_price_override != null && meas.unit_price_override !== "") {
            savedOverrides[w.pc.id] = +meas.unit_price_override;
            overridesChanged = true;
          }
          onRemoveMeasurement(meas.id);
        }
      }
    }
    // Herstel eventueel eerder bewaarde override voor DIT price code.
    const restoredOverride = savedOverrides[pc.id];
    if (restoredOverride != null) {
      delete savedOverrides[pc.id];
      overridesChanged = true;
    }
    if (overridesChanged) onUpdate({ saved_price_overrides: savedOverrides });
    const m = {
      id: crypto.randomUUID(),
      zone_id: zoneId,
      room_id: null,
      // Bij override: gebruik override-tekst zodat PDF/lines de duidelijke
      // context (Dakoversteek / Boeiboord / Kroonlijst) tonen ipv "Gevel …".
      description: override || pc.description,
      formula: "extra_element_qty",
      extra_element_id: elem.id,
      length: 0, width: 0, height: 0, quantity: 0,
      multiplier: 1, coats: 2,
      price_code_id: pc.id,
      action_code: pc.code,
      unit_price_override: restoredOverride != null ? restoredOverride : null,
    };
    onAddMeasurement(m);
  };

  // Wanneer unit, materiaal of kind wijzigt → wis measurements die niet meer
  // toegestaan zijn EN werk descriptions bij die wél blijven (bv. S-06.02
  // switch: hout "gronden / primer" → metaal "metaalprimer"). Overrides op
  // unit_price_override blijven bewaard.
  // v6: overrides op verwijderde measurements worden opgeslagen in
  // element.saved_price_overrides zodat ze bij re-select terugkomen.
  const applyContextToMeasurements = (nextUnit, nextMaterial, nextKind = elem.kind) => {
    const nextItems = resolveWorkItems(nextKind, nextUnit, nextMaterial, priceCodes, elem);
    const allowedById = new Map(nextItems.map((it) => [it.pc.id, it]));
    const savedOverrides = { ...(elem.saved_price_overrides || {}) };
    let overridesChanged = false;
    Object.values(measByPc).forEach((m) => {
      if (!m) return;
      const it = allowedById.get(m.price_code_id);
      if (!it) {
        if (m.unit_price_override != null && m.unit_price_override !== "") {
          savedOverrides[m.price_code_id] = +m.unit_price_override;
          overridesChanged = true;
        }
        onRemoveMeasurement(m.id);
      } else if (m.description !== it.display) {
        onUpdateMeasurement(m.id, { description: it.display });
      }
    });
    if (overridesChanged) onUpdate({ saved_price_overrides: savedOverrides });
  };

  const handleUnitChange = (newUnit) => {
    if (newUnit === unit) return;
    applyContextToMeasurements(newUnit, material);
    // Reset unit-specifieke dimensies naar 0 om spookwaardes te vermijden.
    // qty behouden zodat legacy stuks-records niet verdwijnen.
    const patch = { unit: newUnit };
    if (newUnit !== "m2") {
      patch.calc_mode = "lxw";
    } else if (!elem.calc_mode) {
      patch.calc_mode = "lxw";
    }
    if (newUnit === "forfait") {
      patch.qty = 1;
      patch.dim_length = 0;
      patch.dim_width = 0;
      patch.manual_area = 0;
    }
    onUpdate(patch);
  };

  const handleMaterialChange = (newMat) => {
    if (newMat === material) return;
    applyContextToMeasurements(unit, newMat);
    const patch = { material: newMat };
    if (newMat !== "ander") patch.material_note = "";
    onUpdate(patch);
  };

  const handleKindChange = (newKind) => {
    if (newKind === elem.kind) return;
    // Als de huidige unit niet toegestaan is voor de nieuwe kind → resetten
    // naar default_unit. Applies-to-measurements zorgt voor pruning.
    const kindDef = ELEMENT_KINDS.find((k) => k.value === newKind);
    const nextUnit = kindDef && !kindDef.allowed_units.includes(unit) ? kindDef.default_unit : unit;
    // Fase 2c v5 — als het huidige materiaal niet meer toegelaten is voor de
    // nieuwe kind (bv. Dakoversteek+beton_steen → Boeiboord) → resetten naar
    // "" (gebruiker moet opnieuw kiezen). applyContext ruimt incompatibele
    // werkposten op.
    const nextMaterial = (kindDef && material && !kindDef.allowed_materials.includes(material)) ? "" : material;
    applyContextToMeasurements(nextUnit, nextMaterial, newKind);
    const patch = { kind: newKind, unit: nextUnit };
    if (nextMaterial !== material) {
      patch.material = nextMaterial;
      if (nextMaterial !== "ander") patch.material_note = "";
    }
    if (nextUnit === "forfait") {
      patch.qty = 1;
      patch.dim_length = 0;
      patch.dim_width = 0;
      patch.manual_area = 0;
    }
    onUpdate(patch);
  };

  const totalForElem = worksForElem.reduce((acc, item) => {
    const meas = measByPc[item.pc.id];
    if (!meas) return acc;
    const price = meas.unit_price_override != null && meas.unit_price_override !== ""
      ? +meas.unit_price_override
      : +item.pc.default_price;
    return acc + (price * effectiveQty);
  }, 0);

  return (
    <div
      className="rounded-xl bg-white border border-[#E5E2DC] overflow-hidden"
      data-testid={`facade-extra-element-${zoneId}-${index}`}
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#FAF9F6] text-left"
        data-testid={`facade-extra-toggle-${zoneId}-${index}`}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronRight className="w-4 h-4 text-[#6B8E6B] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#6B8E6B] flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#2C2C2A] truncate">{displayName}</div>
          <div className="text-[10px] text-[#5D7259] uppercase tracking-wider">
            {kindLabel} · {(+effectiveQty || 0).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}
            {material && (
              <> · {MATERIALS.find((m) => m.value === material)?.label || material}</>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-[#5D7259]">Element-totaal</div>
          <div className="font-mono font-bold text-[#6B8E6B] text-sm" data-testid={`facade-extra-total-${zoneId}-${index}`}>
            {fmtEUR(totalForElem)}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="w-8 h-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#D9534F] flex-shrink-0"
          data-testid={`facade-extra-remove-${zoneId}-${index}`}
          aria-label="Verwijder buitenelement"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-[#E5E2DC]">
          {/* Type + Naam */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Type</label>
              <select
                value={elem.kind}
                onChange={(e) => handleKindChange(e.target.value)}
                className="h-9 w-full rounded-lg border border-[#E5E2DC] px-2 text-sm bg-white"
                data-testid={`facade-extra-kind-${zoneId}-${index}`}
              >
                {ELEMENT_KINDS.map((k) => (<option key={k.value} value={k.value}>{k.label}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Naam / omschrijving</label>
              <Input
                value={elem.name || ""}
                onChange={(e) => onUpdate({ name: e.target.value })}
                onFocus={autoSelectOnFocus}
                placeholder={elem.kind === "other" ? "Vrije naam (verplicht)" : `${kindLabel} ${index + 1}`}
                className="h-9 rounded-lg text-sm"
                data-testid={`facade-extra-name-${zoneId}-${index}`}
              />
            </div>
          </div>

          {/* Eenheid + Materiaal */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Eenheid</label>
              <div className="flex gap-1 bg-[#F2F5EE] rounded-lg p-1" data-testid={`facade-extra-unit-${zoneId}-${index}`}>
                {UNITS.filter((u) => visibleUnits.includes(u.value)).map((u) => {
                  const isLegacy = !allowedUnits.includes(u.value);
                  return (
                    <button
                      key={u.value}
                      type="button"
                      onClick={() => handleUnitChange(u.value)}
                      className={`flex-1 px-2 h-8 rounded-md text-xs font-medium transition ${unit === u.value ? "bg-[#6B8E6B] text-white shadow" : "text-[#4F4F4D] hover:bg-white"} ${isLegacy && unit !== u.value ? "opacity-60" : ""}`}
                      data-testid={`facade-extra-unit-${u.value}-${zoneId}-${index}`}
                      title={isLegacy ? "Legacy eenheid (behouden voor bestaande records)" : ""}
                    >
                      {u.label}{isLegacy ? "*" : ""}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Materiaal</label>
              <select
                value={material}
                onChange={(e) => handleMaterialChange(e.target.value)}
                className="h-9 w-full rounded-lg border border-[#E5E2DC] px-2 text-sm bg-white"
                data-testid={`facade-extra-material-${zoneId}-${index}`}
              >
                {MATERIALS.filter((m) => visibleMaterials.includes(m.value)).map((m) => {
                  const isLegacy = m.value && !allowedMaterials.includes(m.value);
                  const label = (m.value === "metaal" && elem.kind === "boeiboord") ? "Metaal / aluminium" : m.label;
                  return (
                    <option key={m.value || "none"} value={m.value}>
                      {label}{isLegacy ? "  (legacy)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Materiaal-vrijetekst bij 'ander' */}
          {material === "ander" && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Materiaal-omschrijving</label>
              <Input
                value={elem.material_note || ""}
                onChange={(e) => onUpdate({ material_note: e.target.value })}
                placeholder="Bv. 'Composiet' of 'Trespa'"
                className="h-9 rounded-lg text-sm"
                data-testid={`facade-extra-material-note-${zoneId}-${index}`}
              />
            </div>
          )}

          {/* Unit-specifieke invoervelden */}
          {unit === "lm" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">
                  Lengte (m)
                </label>
                <RawNumInput
                  value={elem.dim_length ?? 0}
                  onChange={(v) => onUpdate({ dim_length: typeof v === "number" ? v : 0 })}
                  className="h-9 w-full rounded-lg text-right font-mono text-sm"
                  data-testid={`facade-extra-dim-length-${zoneId}-${index}`}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">
                  Berekende hoeveelheid
                </label>
                <div
                  className="h-9 w-full rounded-lg border border-[#E5E2DC] bg-[#FAFAF7] px-3 flex items-center justify-end font-mono text-sm text-[#2C2C2A]"
                  data-testid={`facade-extra-computed-qty-${zoneId}-${index}`}
                >
                  {effectiveQty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}
                </div>
              </div>
            </div>
          )}

          {unit === "m2" && (
            <div className="space-y-2">
              {/* calc_mode toggle */}
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Berekeningswijze</label>
                <div className="flex gap-1 bg-[#F2F5EE] rounded-lg p-1" data-testid={`facade-extra-calcmode-${zoneId}-${index}`}>
                  <button
                    type="button"
                    onClick={() => onUpdate({ calc_mode: "lxw" })}
                    className={`flex-1 px-2 h-8 rounded-md text-xs font-medium transition ${calcMode === "lxw" ? "bg-[#6B8E6B] text-white shadow" : "text-[#4F4F4D] hover:bg-white"}`}
                    data-testid={`facade-extra-calcmode-lxw-${zoneId}-${index}`}
                  >
                    L × B
                  </button>
                  <button
                    type="button"
                    onClick={() => onUpdate({ calc_mode: "manual" })}
                    className={`flex-1 px-2 h-8 rounded-md text-xs font-medium transition ${calcMode === "manual" ? "bg-[#6B8E6B] text-white shadow" : "text-[#4F4F4D] hover:bg-white"}`}
                    data-testid={`facade-extra-calcmode-manual-${zoneId}-${index}`}
                  >
                    Handmatige m²
                  </button>
                </div>
              </div>

              {calcMode === "lxw" ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Lengte (m)</label>
                    <RawNumInput
                      value={elem.dim_length ?? 0}
                      onChange={(v) => onUpdate({ dim_length: typeof v === "number" ? v : 0 })}
                      className="h-9 w-full rounded-lg text-right font-mono text-sm"
                      data-testid={`facade-extra-dim-length-${zoneId}-${index}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Breedte (m)</label>
                    <RawNumInput
                      value={elem.dim_width ?? 0}
                      onChange={(v) => onUpdate({ dim_width: typeof v === "number" ? v : 0 })}
                      className="h-9 w-full rounded-lg text-right font-mono text-sm"
                      data-testid={`facade-extra-dim-width-${zoneId}-${index}`}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Oppervlakte</label>
                    <div
                      className="h-9 w-full rounded-lg border border-[#E5E2DC] bg-[#FAFAF7] px-3 flex items-center justify-end font-mono text-sm text-[#2C2C2A]"
                      data-testid={`facade-extra-computed-qty-${zoneId}-${index}`}
                    >
                      {effectiveQty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Oppervlakte (m²)</label>
                    <RawNumInput
                      value={elem.manual_area ?? 0}
                      onChange={(v) => onUpdate({ manual_area: typeof v === "number" ? v : 0 })}
                      className="h-9 w-full rounded-lg text-right font-mono text-sm"
                      data-testid={`facade-extra-manual-area-${zoneId}-${index}`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Berekende hoeveelheid</label>
                    <div
                      className="h-9 w-full rounded-lg border border-[#E5E2DC] bg-[#FAFAF7] px-3 flex items-center justify-end font-mono text-sm text-[#2C2C2A]"
                      data-testid={`facade-extra-computed-qty-${zoneId}-${index}`}
                    >
                      {effectiveQty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {unit === "stuks" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Aantal (stuk)</label>
                <RawNumInput
                  value={elem.qty ?? 0}
                  onChange={(v) => onUpdate({ qty: typeof v === "number" ? v : 0 })}
                  className="h-9 w-full rounded-lg text-right font-mono text-sm"
                  data-testid={`facade-extra-qty-${zoneId}-${index}`}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Berekende hoeveelheid</label>
                <div
                  className="h-9 w-full rounded-lg border border-[#E5E2DC] bg-[#FAFAF7] px-3 flex items-center justify-end font-mono text-sm text-[#2C2C2A]"
                  data-testid={`facade-extra-computed-qty-${zoneId}-${index}`}
                >
                  {effectiveQty.toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} stuk
                </div>
              </div>
            </div>
          )}

          {unit === "forfait" && (
            <div className="rounded-lg border border-[#E5E2DC] bg-[#FAFAF7] px-3 py-2 text-xs text-[#5D7259] italic" data-testid={`facade-extra-forfait-note-${zoneId}-${index}`}>
              Forfait wordt automatisch als <strong>1</strong> gerekend. Vink hieronder <strong>Forfait</strong> aan en typ zelf de projectprijs — die is inclusief materiaal &amp; werkuren.
            </div>
          )}

          {/* Notitie */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">Notitie (optioneel)</label>
            <Input
              value={elem.notes || ""}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              placeholder="Bv. 'Ijzeren beslag te schilderen'"
              className="h-9 rounded-lg text-sm"
              data-testid={`facade-extra-notes-${zoneId}-${index}`}
            />
          </div>

          {/* Werkposten */}
          <div className="border border-[#E5E2DC] rounded-lg overflow-hidden divide-y divide-[#F0EFEA] bg-white">
            {(!material && unit !== "forfait") ? (
              <div
                className="p-3 text-[12px] text-[#5D7259] italic"
                data-testid={`facade-extra-material-hint-${zoneId}-${index}`}
              >
                Kies eerst het materiaal om de geschikte behandelingen te tonen.
              </div>
            ) : worksForElem.length === 0 ? (
              <div className="p-3 text-[11px] text-[#5D7259] italic">
                Geen werkposten beschikbaar voor deze combinatie ({kindLabel} · {unitLabel}
                {material ? ` · ${MATERIALS.find((m) => m.value === material)?.label}` : ""}).
                Wijzig eenheid of materiaal, of gebruik &quot;Ander buitenelement&quot;.
              </div>
            ) : (
              worksForElem.map((item, i) => {
                const prev = worksForElem[i - 1];
                const isFirstInGroup = item.choice_group && (!prev || prev.choice_group !== item.choice_group);
                return (
                  <React.Fragment key={item.pc.id}>
                    {isFirstInGroup && (
                      <div
                        className="px-3 py-1 bg-[#F2F5EE] text-[9px] uppercase tracking-[0.15em] font-bold text-[#3E5A3E] flex items-center gap-2"
                        data-testid={`facade-extra-choice-group-header-${zoneId}-${index}-${item.choice_group}`}
                      >
                        <span>{CHOICE_GROUP_LABELS[item.choice_group] || "Kies één"}</span>
                      </div>
                    )}
                    <ExtraWorkLine
                      pc={item.pc}
                      displayDescription={item.display}
                      measurement={measByPc[item.pc.id]}
                      elementQty={effectiveQty}
                      unitLabel={unitLabel}
                      zoneId={zoneId}
                      elemId={elem.id}
                      onToggle={() => toggleWork(item)}
                      onUpdateMeasurement={onUpdateMeasurement}
                    />
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Wrapper: hele buitenelementen-blok binnen een zone ---
export default function FacadeExtraElementBlock({ zone, priceCodes, measurements, onUpdateZone, onAddMeasurement, onUpdateMeasurement, onRemoveMeasurement, legacyMode = false }) {
  const elements = zone.facade_extra_elements || [];

  const updateElement = (idx, patch) => {
    const next = elements.map((el, i) => (i === idx ? { ...el, ...patch } : el));
    onUpdateZone({ facade_extra_elements: next });
  };
  const removeElement = (idx) => {
    const elem = elements[idx];
    // Verwijder alle measurements die aan dit element gekoppeld zijn
    if (elem) {
      const linked = measurements.filter((m) => m.extra_element_id === elem.id);
      for (const m of linked) onRemoveMeasurement(m.id);
    }
    const next = elements.filter((_, i) => i !== idx);
    onUpdateZone({ facade_extra_elements: next });
  };
  const addElement = () => {
    // Fase 2c v3: nieuwe elementen krijgen de default_unit van hun kind
    // (dakoversteek default = m²; boeiboord/kroonlijst = lm).
    // v6 F42k Correctieronde v2 — auto-generated name zodat de "select-on-focus"
    // UX werkt (klik op naamveld → alles geselecteerd → typ = overschrijven).
    const defKind = ELEMENT_KINDS[0];
    const autoName = defKind.value === "other" ? "" : `${defKind.label} ${elements.length + 1}`;
    const next = [
      ...elements,
      {
        id: crypto.randomUUID(),
        name: autoName,
        kind: defKind.value,
        unit: defKind.default_unit,
        qty: 0,
        dim_length: 0,
        dim_width: 0,
        manual_area: 0,
        calc_mode: "lxw",
        material: "",
        material_note: "",
        notes: "",
      },
    ];
    onUpdateZone({ facade_extra_elements: next });
  };

  return (
    <div
      className="mb-3 p-3 rounded-xl bg-[#FAFAF7] border border-[#E5E2DC] space-y-3"
      data-testid={`facade-extra-block-${zone.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#6B8E6B]">
          {legacyMode ? "Aanvullende buitenelementen (LEGACY)" : "Aanvullende buitenelementen"} ({elements.length})
        </div>
        <div className="text-[10px] text-[#5D7259] italic">
          {legacyMode
            ? "Legacy — nieuwe elementen toevoegen via zone \"Buitenelementen\""
            : "Tellen niet mee in gevel-m² · dakoversteek · boeiboord · kroonlijst"}
        </div>
      </div>
      {legacyMode && (
        <div
          className="text-[11px] text-[#7F4124] bg-[#FFF3E8] border border-[#F2C79C] rounded-md px-3 py-2"
          data-testid={`facade-extra-legacy-banner-${zone.id}`}
        >
          Deze buitenzone bevat oudere aanvullende buitenelementen. Bestaande records blijven bewerkbaar hier.
          Voor nieuwe buitenelementen: gebruik een aparte zone <b>Buitenelementen</b> (met automatische groepering).
        </div>
      )}

      {elements.length > 0 && (
        <div className="space-y-2" data-testid={`facade-extra-list-${zone.id}`}>
          {elements.map((el, idx) => (
            <FacadeExtraElementCard
              key={el.id || idx}
              elem={el}
              index={idx}
              priceCodes={priceCodes}
              measurements={measurements}
              zoneId={zone.id}
              defaultCollapsed={idx > 0 && elements.length > 1}
              onUpdate={(patch) => updateElement(idx, patch)}
              onRemove={() => removeElement(idx)}
              onAddMeasurement={onAddMeasurement}
              onUpdateMeasurement={onUpdateMeasurement}
              onRemoveMeasurement={onRemoveMeasurement}
            />
          ))}
        </div>
      )}

      {!legacyMode && (
        <button
          type="button"
          onClick={addElement}
          className="w-full text-sm px-3 h-10 rounded-lg border-2 border-dashed border-[#6B8E6B]/50 hover:border-[#6B8E6B] hover:bg-white text-[#3E5A3E] font-medium gap-1 inline-flex items-center justify-center"
          data-testid={`facade-extra-add-${zone.id}`}
        >
          <Plus className="w-4 h-4" /> Buitenelement toevoegen
        </button>
      )}
    </div>
  );
}
